import { Fragment } from "react";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import List from "@ui-kit/data-display/List/List.web";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import AssignedSeatInformation from "@shared/modules/seating/components/AssignedSeatInformation/AssignedSeatInformation.web";
import { useTranslation } from "@shared/utils/internationalization";
import ListItem from "@ui-kit/data-display/ListItem/ListItem.web";
import Button from "@ui-kit/form/Button/Button.web";
import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { BaseItemGroupDisplayProps } from "../ItemGroup.props";

type SeatAndLocationType = {
  id: number;
  identifier?: string;
  segment_identifier?: string;
  location_identifier?: string;
  is_accessible?: boolean;
};
export default function ItemGroupAssignedSeats({
  seatPermissions = [],
  locationPermissions = [],
  handleRemoveAssignedSeat,
  isBasketUpdating = false,
  allowQuantityChange = true,
}: {
  seatPermissions: BaseItemGroupDisplayProps["seatPermissions"];
  locationPermissions: BaseItemGroupDisplayProps["locationPermissions"];
  handleRemoveAssignedSeat?: (seatOrLocation: SeatAndLocationType) => void;
  isBasketUpdating?: boolean;
  allowQuantityChange?: boolean;
}) {
  const { isOpen, onToggle } = useDisclosure();
  const permissions = [...seatPermissions, ...locationPermissions];
  const { t } = useTranslation("common");

  const [first3Seats, remainingSeats] =
    permissions && permissions.length > 0
      ? [permissions.slice(0, 3), permissions.slice(3)]
      : [];

  return (
    <>
      {first3Seats && first3Seats.length > 0 && (
        <Flex direction="column" gap="md">
          <List spacing="8px">
            {first3Seats.map((permission) => {
              const seatOrLocation = (permission?.seat ||
                permission?.location) as SeatAndLocationType;
              return (
                <ListItem
                  key={`seatOrLocation_${seatOrLocation?.id}`}
                  display="block"
                  cursor="default"
                  backgroundColor="gray.200"
                >
                  <Box
                    backgroundColor="gray.200"
                    borderRadius="sm"
                    padding="4px 8px"
                    display="flex"
                    justifyContent="space-between"
                  >
                    <AssignedSeatInformation
                      identifier={seatOrLocation?.identifier}
                      segment_identifier={seatOrLocation?.segment_identifier}
                      location_identifier={seatOrLocation?.location_identifier}
                      isAccessible={seatOrLocation?.is_accessible}
                    />
                    <Box cursor={allowQuantityChange ? "pointer" : "default"}>
                      {allowQuantityChange && handleRemoveAssignedSeat && (
                        <IconButton.Ghost
                          data-testid={`remove-assigned-seat-${seatOrLocation?.id}`}
                          ml="auto"
                          aria-label="remove-assigned-seat"
                          iconName="Trash"
                          isDisabled={isBasketUpdating}
                          onClick={() => handleRemoveAssignedSeat(permission)}
                          iconColor="font.dark"
                          iconSize="20px"
                          size="xs"
                        />
                      )}
                    </Box>
                  </Box>
                  {seatOrLocation?.is_accessible && (
                    <Flex ml="2xs" mb="sm">
                      <Text fontSize="sm" color="gray.800">
                        {t(
                          "Accessible Seat. Please confirm your accessibility needs before purchasing."
                        )}
                      </Text>
                    </Flex>
                  )}
                </ListItem>
              );
            })}

            {isOpen &&
              remainingSeats &&
              remainingSeats.length > 0 &&
              remainingSeats.map((permission) => {
                const seatOrLocation = (permission?.seat ||
                  permission?.location) as SeatAndLocationType;
                return (
                  <ListItem
                    key={`seatOrLocation_${seatOrLocation?.id}`}
                    bgColor="gray.200"
                    borderRadius="sm"
                    padding="4px 8px"
                    display="flex"
                  >
                    <AssignedSeatInformation
                      identifier={seatOrLocation?.identifier}
                      segment_identifier={seatOrLocation?.segment_identifier}
                      location_identifier={seatOrLocation?.location_identifier}
                      isAccessible={seatOrLocation?.is_accessible}
                    />
                    {allowQuantityChange && handleRemoveAssignedSeat && (
                      <IconButton.Ghost
                        data-testid={`remove-assigned-seat-${seatOrLocation?.id}`}
                        ml="auto"
                        aria-label="remove-assigned-seat"
                        iconName="Trash"
                        isDisabled={isBasketUpdating}
                        onClick={() => handleRemoveAssignedSeat(permission)}
                        iconColor="font.dark"
                        iconSize="20px"
                        size="xs"
                      />
                    )}
                  </ListItem>
                );
              })}
          </List>
        </Flex>
      )}
      {remainingSeats && remainingSeats.length > 0 && (
        <Button.Ghost
          data-testid="item-group-hide-show-seats"
          onClick={onToggle}
          m="auto"
        >
          {isOpen
            ? t("Hide Seats")
            : t("View all {{ count }} seats", {
                count: permissions?.length,
              })}
        </Button.Ghost>
      )}
    </>
  );
}
